import {
  useMutation,
  useQuery,
  type QueryKey
} from "@tanstack/react-query";
import { ERRORS } from "../consts";
import {
  fetcherFactory
} from "../services";
import { useAuthStore } from "../stores";
import type { EditorialDto } from "../types";
import { getTimezoneFormattedDate } from "../utils/datetime";
import {
  getEnvironmentVariables
} from "../utils/general";

type useGetEditorialQueryProps = {
  idEditorial: number,
  language?: string
}

type useSaveEditorialQueryProps = {
  data: string,
  html: string,
  name: string,
  initiative_id: number,
  corporate_id: number,
  language?: string
}

const { basePath } = getEnvironmentVariables();

export function useGetEditorialQuery({ idEditorial, language }: useGetEditorialQueryProps) {

  const fetcher = fetcherFactory();

  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);

  const uri = `/learning-catalogue/editorials/${idEditorial}`;

  return useQuery<
      unknown,
      unknown,
      EditorialDto,
      QueryKey
    >({
      queryFn: () => fetcher(
        `${basePath}${uri}`,
        {
          headers: {
            "Accept-Language": language || "",
            "authorization": accessToken,
            "x-ada-session-token": sessionToken,
            "x-user-timezone": getTimezoneFormattedDate()
          },
          method: "GET"
        })
        .then((res) => {
          return res.json();
        })
        .then((data: EditorialDto) => (
          data
        ))
        .catch((error) => {
          if (error === ERRORS.UNAUTHORIZED) {
            setUnauthorized(true);
            throw error; // rethrow so that react query doesn't complain about undefined return value
          } else {
            throw error;
          }
        }),
      queryKey: [idEditorial],
      refetchOnMount: true,
      staleTime: 1000 * 10
    });
}

export function useSaveEditorialQuery({ data, html, name, initiative_id, corporate_id, language }: 
  useSaveEditorialQueryProps) {

  const fetcher = fetcherFactory();

  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);

  const uri = "/learning-catalogue/editorials";

  const body = { data, html, name, initiative_id, corporate_id };

  return useMutation({
    mutationFn: () => fetcher(
      `${basePath}${uri}`,
      {
        body: JSON.stringify(body),
        headers: {
          "Accept-Language": language || "",
          "authorization": accessToken,
          "x-ada-session-token": sessionToken,
          "x-user-timezone": getTimezoneFormattedDate()
        },
        method: "POST"
      })
      .then((res) => {
        return res.json();
      })
      .then((data: EditorialDto) => (
        data
      ))
      .catch((error) => {
        if (error === ERRORS.UNAUTHORIZED) {
          setUnauthorized(true);
          throw error; // rethrow so that react query doesn't complain about undefined return value
        } else {
          throw error;
        }
      })
  });
}
